import { auth } from './firebase';

// Quitamos trailing slashes: si VITE_API_BASE viene con `/` final (típico al
// configurarla en Cloudflare Pages env vars), concatenar con un path que
// empieza por `/` produce `//api/...` y el worker devuelve 404.
const PRIMARY = (import.meta.env.VITE_API_BASE || 'https://ws.tikcontrol.live')
  .replace(/\/+$/, '');

// Puente anti-bloqueos (22-ago-2026). Los ISPs españoles bloquean las IPs de
// Cloudflare durante los partidos de LaLiga; el backoffice se sirve entonces
// por el VPS (backoffice.tikcontrol.live flipeado al VPS por el Worker), pero
// sus llamadas a ws.tikcontrol.live seguían colgándose. Mismo patrón que la
// app: si el primario no responde y el puente sí, todo va por el puente
// (ws.tikcontrol.es → Caddy del VPS → el mismo Worker). Cualquier respuesta
// HTTP cuenta como "vivo": el bloqueo se manifiesta como fallo de conexión,
// nunca como status code.
const BRIDGE = (import.meta.env.VITE_API_BRIDGE || 'https://ws.tikcontrol.es')
  .replace(/\/+$/, '');
const PROBE_TIMEOUT_MS = 4000;

let apiBase = PRIMARY;
let probed = null;

function isNetworkError(e) {
  return !!e && (e.name === 'AbortError' || e.name === 'TimeoutError' || e.name === 'TypeError');
}

function timeoutSignal(ms) {
  return typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function'
    ? AbortSignal.timeout(ms)
    : undefined;
}

async function probe(base) {
  await fetch(`${base}/failover/status`, { cache: 'no-store', signal: timeoutSignal(PROBE_TIMEOUT_MS) });
}

// Sondeo inicial, una sola vez por carga: primario vivo → se queda; primario
// muerto y puente vivo → puente. Sin primario ni puente → primario (no hay
// red; que falle con su error normal).
function ensureBase() {
  if (!probed) {
    probed = (async () => {
      if (PRIMARY === BRIDGE) return;
      try { await probe(PRIMARY); return; } catch (e) { if (!isNetworkError(e)) return; }
      try {
        await probe(BRIDGE);
        apiBase = BRIDGE;
        console.warn('[api] ws.tikcontrol.live no responde → usando el puente', BRIDGE);
      } catch (_e) { /* sin red: nos quedamos en el primario */ }
    })();
  }
  return probed;
}

/** Base activa del Worker (primario o puente). */
export function getApiBase() {
  return apiBase;
}

// fetch con alternancia: si la base activa falla por RED (no por status),
// reintenta una vez en la otra y se queda con ella. Un bloqueo que empieza a
// mitad de sesión se resuelve así sin recargar.
async function fetchWithFailover(path, init) {
  await ensureBase();
  const first = apiBase;
  try {
    return await fetch(`${first}${path}`, init);
  } catch (e) {
    if (!isNetworkError(e) || PRIMARY === BRIDGE) throw e;
    const other = first === PRIMARY ? BRIDGE : PRIMARY;
    const res = await fetch(`${other}${path}`, init);
    apiBase = other;
    console.warn('[api] error de red en', first, '→ cambiando a', other);
    return res;
  }
}

async function authHeaders() {
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in');
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

export async function apiFetch(path, { method = 'GET', body, headers = {}, cache } = {}) {
  const authHdrs = await authHeaders();
  const res = await fetchWithFailover(path, {
    method,
    cache,
    headers: {
      'Content-Type': 'application/json',
      ...authHdrs,
      ...headers
    },
    body: body ? JSON.stringify(body) : undefined
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${res.status} ${res.statusText}: ${text}`);
  }
  if (res.status === 204) return null;
  const ct = res.headers.get('content-type') || '';
  return ct.includes('application/json') ? res.json() : res.text();
}

/**
 * Igual que apiFetch pero sin lanzar: devuelve { ok, status, data }.
 * Necesario cuando el endpoint responde un error con payload útil (por
 * ejemplo un 409 con la lista de conflictos), que apiFetch aplanaría a
 * un string dentro del mensaje del Error.
 */
export async function apiTry(path, { method = 'GET', body, headers = {} } = {}) {
  const authHdrs = await authHeaders();
  const res = await fetchWithFailover(path, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...authHdrs,
      ...headers
    },
    body: body ? JSON.stringify(body) : undefined
  });
  let data = null;
  const ct = res.headers.get('content-type') || '';
  if (res.status !== 204) {
    data = ct.includes('application/json')
      ? await res.json().catch(() => null)
      : await res.text().catch(() => null);
  }
  return { ok: res.ok, status: res.status, data };
}

export async function apiUpload(path, formData, { method = 'POST', headers = {} } = {}) {
  const authHdrs = await authHeaders();
  const res = await fetchWithFailover(path, {
    method,
    headers: {
      ...authHdrs,
      ...headers
    },
    body: formData
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${res.status} ${res.statusText}: ${text}`);
  }
  if (res.status === 204) return null;
  const ct = res.headers.get('content-type') || '';
  return ct.includes('application/json') ? res.json() : res.text();
}
